import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  FiChevronDown,
  FiChevronRight,
  FiEdit2,
  FiTrash2,
  FiCopy,
  FiPlus,
  FiArrowUp,
  FiArrowDown,
  FiSave,
  FiCheck,
  FiX,
  FiEye,
  FiMonitor,
  FiSmartphone,
} from "react-icons/fi";
import { FaSpinner } from "react-icons/fa";
import FieldConfigDrawer from "./FieldConfigDrawer";
import FieldTypeSelector from "./FieldTypeSelector";
import FormPreviewPanel from "./FormPreviewPanel";
import {
  FIELD_TYPE_MAP,
  FORM_CATEGORIES,
  isLockedFormField,
  isCommonBasicSection,
  getSectionServices,
  isAlwaysEnabledSection,
  getPlanFormCategories,
  getPlanServiceInterestOptions,
  NEUTERED_STATUS_OPTIONS,
} from "./boardingFormTemplate";
import { useProfile } from "../../context/ProfileContext";
import { toast } from "react-toastify";


// Fields that gate a whole section when disabled.
const GATE_MAPS_TO = new Set([
  "pet.has_medical_condition",
  "pet.on_medication",
]);

const isGateField = (f) => {
  if (!f) return false;
  if (f.gatesSection === true) return true;
  const m = f.mapsTo || f.maps_to;
  return GATE_MAPS_TO.has(m);
};
// ─── Tiny helpers ─────────────────────────────────────────────────────────────
let _cid = 1;
const cid = (prefix = "c") => `${prefix}-${_cid++}-${Date.now()}`;

// ─── Delete confirmation modal ────────────────────────────────────────────────
function ConfirmModal({ open, title, message, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4">
        <h3 className="text-base font-semibold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-5">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Gate-warning modal (fires only for section-gating Yes/No fields) ─────────
function GateWarningModal({ open, field, onConfirm, onCancel }) {
  if (!open || !field) return null;

  const title =
    field.sectionDisableTitle || "Disable this section?";
  const message =
    field.sectionDisableMessage ||
    "Other fields in this section depend on this question. If you disable it, all remaining fields in the section will be disabled too.";

  const dependents = Array.isArray(field.dependentFieldLabels)
    ? field.dependentFieldLabels
    : [];

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-800 mb-1.5">
              {title}
            </h3>
            <p className="text-sm text-gray-600">{message}</p>

            {dependents.length > 0 && (
              <ul className="mt-3 space-y-1 rounded-lg bg-gray-50 p-3 text-[12px] text-gray-600">
                {dependents.map((lbl) => (
                  <li key={lbl} className="flex items-center gap-1.5">
                    <span className="h-1 w-1 rounded-full bg-gray-400" />
                    {lbl}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Yes, disable section
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Inline editable section header ──────────────────────────────────────────
function SectionHeader({
  section,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onToggleCollapse,
  isCustomerOnboarding,
  allowedServices = ["Boarding", "Grooming", "Veterinary", "Other"],
}) {
  const [editing, setEditing] = useState(false);
  const [nameVal, setNameVal] = useState(section.sectionName);
  const [descVal, setDescVal] = useState(section.sectionDescription || "");
  const [servicesVal, setServicesVal] = useState(getSectionServices(section));
  const hasLockedFields = (section.fields || []).some(isLockedFormField);
  const isCommonSection = isCommonBasicSection(section);
  const sectionLocked = isAlwaysEnabledSection(section);

  /* Common/basic sections (Customer Address, Pet Basic Info,
     Customer / Pet Parent Info) never show services. */
  const shouldShowServices = !isCommonSection;

 const ALL_SERVICES = allowedServices;

 

  const allowedSetForDefaults = new Set(allowedServices || []);
  const defaults = getSectionServices(section).filter((srv) =>
    allowedSetForDefaults.has(srv),
  );

  const startEdit = () => {
    setNameVal(section.sectionName);
    setDescVal(section.sectionDescription || "");
    setServicesVal(isCommonSection ? [] : defaults);
    setEditing(true);
  };

     const toggleService = (srv) => {
       setServicesVal((prev) =>
         prev.includes(srv) ? prev.filter((s) => s !== srv) : [...prev, srv],
       );
     };

  const saveEdit = () => {
    const finalServices = isCommonSection
      ? []
      : servicesVal.filter((svc) => allowedSetForDefaults.has(svc));
    onUpdate({
      ...section,
      sectionName: nameVal.trim() || section.sectionName,
      sectionDescription: descVal,
      associated_services: finalServices,
      associatedServices: finalServices,
      associatedService: finalServices.join(", "),
    });
    setEditing(false);
  };

  const sectionServices = getSectionServices(section);

  
  const allowedSet = new Set(allowedServices || []);
  const displayServices = sectionServices.filter((srv) => allowedSet.has(srv));

  const sectionEnabled = section.enabled !== false;

  return (
    <div className="flex items-start gap-3 w-full">
      {/* Collapse toggle */}
      <button
        onClick={onToggleCollapse}
        className="mt-0.5 text-gray-400 hover:text-gray-700 shrink-0"
      >
        {section.collapsed ? (
          <FiChevronRight className="w-4 h-4" />
        ) : (
          <FiChevronDown className="w-4 h-4" />
        )}
      </button>

      {/* Name / description */}
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="space-y-2">
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                Section Title
              </label>
              <input
                autoFocus
                value={nameVal}
                onChange={(e) => setNameVal(e.target.value)}
                className="w-full border border-blue-400 rounded-md px-3 py-1.5 text-sm font-semibold focus:outline-none"
                placeholder="Section name"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                Description
              </label>
              <input
                value={descVal}
                onChange={(e) => setDescVal(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-xs focus:outline-none"
                placeholder="Section description (optional)"
              />
            </div>
            {shouldShowServices && (
              <div>
                <label className="block text-[11px] font-medium text-gray-600 mb-1">
                  Associated Services (Multi-select)
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {ALL_SERVICES.map((srv) => {
                    const isSelected = servicesVal.includes(srv);
                    return (
                      <button
                        key={srv}
                        type="button"
                        onClick={() => toggleService(srv)}
                        className={`px-2.5 py-1 text-xs font-medium rounded-lg border transition-all ${
                          isSelected
                            ? "bg-[#1B4582] text-white border-[#1B4582] shadow-sm"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {isSelected ? `✓ ${srv}` : `+ ${srv}`}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button
                onClick={saveEdit}
                className="flex items-center gap-1 px-3 py-1 text-xs bg-[#1B4582] text-white rounded-md hover:bg-[#163a6e] transition-colors"
              >
                <FiCheck className="w-3 h-3" /> Save
              </button>
              <button
                onClick={() => setEditing(false)}
                className="flex items-center gap-1 px-3 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <FiX className="w-3 h-3" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            {/* Section name + inline badges + hint */}
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-sm font-semibold ${
                  isCustomerOnboarding && !sectionEnabled
                    ? "text-gray-400"
                    : "text-gray-800"
                }`}
              >
                {section.sectionName}
              </span>

              {shouldShowServices &&
                displayServices.map((srv) => (
                  <span
                    key={srv}
                    className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                  >
                    {srv}
                  </span>
                ))}

              {isCustomerOnboarding && shouldShowServices && (
                <span className="text-[11px] font-bold text-gray-700">
                  Click the Edit (pencil) icon to modify the service selection
                  as required.
                </span>
              )}

              {isCustomerOnboarding && !sectionEnabled && (
                <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                  Disabled
                </span>
              )}
            </div>

            {section.sectionDescription && (
              <span className="text-xs text-gray-500 block mt-0.5">
                {section.sectionDescription}
              </span>
            )}
            <span
              className={`text-xs mt-0.5 block ${
                isCustomerOnboarding && !sectionEnabled
                  ? "text-gray-300"
                  : "text-gray-400"
              }`}
            >
              {(section.fields || []).length} field
              {(section.fields || []).length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {/* Controls */}
      {!editing && (
        <div className="flex items-center gap-1 shrink-0">
          {isCustomerOnboarding && (
            <div className="flex items-center gap-1.5 mr-1">
              {sectionLocked ? (
                <span className="px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded">
                  Always Enabled
                </span>
              ) : (
                <>
                  <span className="text-xs font-medium text-gray-500 hidden sm:inline">
                    {sectionEnabled ? "Enabled" : "Disabled"}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const nextEnabled = !sectionEnabled;
                      onUpdate({
                        ...section,
                        enabled: nextEnabled,
                        is_enabled: nextEnabled,
                        isEnabled: nextEnabled,
                        /* flip every non-locked field to match the section */
                        fields: (section.fields || []).map((f) =>
                          isLockedFormField(f)
                            ? f
                            : {
                                ...f,
                                enabled: nextEnabled,
                                is_enabled: nextEnabled,
                                isEnabled: nextEnabled,
                              },
                        ),
                      });
                    }}
                    title={
                      sectionEnabled
                        ? "Click to disable this section and all its fields"
                        : "Click to enable this section and all its fields"
                    }
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      sectionEnabled ? "bg-emerald-500" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform ${
                        sectionEnabled ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </>
              )}
            </div>
          )}

          <button
            title="Move up"
            onClick={onMoveUp}
            disabled={isFirst}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 disabled:opacity-30 transition-colors"
          >
            <FiArrowUp className="w-3.5 h-3.5" />
          </button>
          <button
            title="Move down"
            onClick={onMoveDown}
            disabled={isLast}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 disabled:opacity-30 transition-colors"
          >
            <FiArrowDown className="w-3.5 h-3.5" />
          </button>
          <button
            title="Edit section"
            onClick={startEdit}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 transition-colors"
          >
            <FiEdit2 className="w-3.5 h-3.5" />
          </button>
          {!hasLockedFields && (
            <button
              title="Delete section"
              onClick={onDelete}
              className="p-1.5 rounded-md hover:bg-red-50 text-red-400 transition-colors"
            >
              <FiTrash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Field row ────────────────────────────────────────────────────────────────
function FieldRow({ field, onEdit, onDelete, onDuplicate, onMoveUp, onMoveDown, onToggleEnabled, isFirst, isLast }) {
  const typeMeta = FIELD_TYPE_MAP[field.fieldType];
  const locked = isLockedFormField(field);
  const disableAllowed = !locked && field.disableAllowed !== false && field.label !== "Phone Number";

  return (
    <div className={`group flex items-center justify-between gap-3 px-4 py-3 bg-white border border-gray-100 rounded-xl hover:border-[#1B4582]/30 hover:shadow-sm transition-all ${!field.enabled ? "opacity-60 bg-gray-50/50" : ""}`}>
      {/* Left: Info */}
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {/* Type badge */}
        <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 text-xs font-bold shrink-0">
          {typeMeta?.icon || "?"}
        </span>

        {/* Info text */}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate">
            {field.label || <span className="text-gray-400 italic">Untitled Field</span>}
            {field.required && <span className="ml-1 text-red-500 font-bold">*</span>}
          </p>
          <p className="text-xs text-gray-400 truncate">
            {typeMeta?.label || field.fieldType}
            {locked && " · System field"}
            {!field.enabled && " · Disabled"}
          </p>
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2 shrink-0">
        {/* Enabled / Disabled toggle switch on field row */}
        {disableAllowed ? (
          <div className="flex items-center gap-1.5 mr-1">
            <span className="text-xs font-medium text-gray-500 hidden sm:inline">
              {field.enabled !== false ? "Enabled" : "Disabled"}
            </span>
            <button
              type="button"
              onClick={onToggleEnabled}
              title={field.enabled !== false ? "Click to Disable field" : "Click to Enable field"}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                field.enabled !== false ? "bg-emerald-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform ${
                  field.enabled !== false ? "translate-x-4" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
        ) : (
          <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            Always Enabled
          </span>
        )}

        {/* Reorder up/down */}
        <button
          onClick={onMoveUp}
          disabled={isFirst}
          title="Move up"
          className="p-1.5 rounded hover:bg-gray-100 text-gray-400 disabled:opacity-30 transition-colors"
        >
          <FiArrowUp className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onMoveDown}
          disabled={isLast}
          title="Move down"
          className="p-1.5 rounded hover:bg-gray-100 text-gray-400 disabled:opacity-30 transition-colors"
        >
          <FiArrowDown className="w-3.5 h-3.5" />
        </button>

        {/* Edit Button */}
        <button
          onClick={onEdit}
          title="Edit field settings"
          className="p-1.5 rounded hover:bg-blue-50 text-blue-500 transition-colors"
        >
          <FiEdit2 className="w-3.5 h-3.5" />
        </button>

        {/* Duplicate */}
        {!locked && (
          <button
            onClick={onDuplicate}
            title="Duplicate field"
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <FiCopy className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Delete */}
        {!locked && (
          <button
            onClick={onDelete}
            title="Delete field"
            className="p-1.5 rounded hover:bg-red-50 text-red-400 transition-colors"
          >
            <FiTrash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main Form Builder ────────────────────────────────────────────────────────
export default function FormBuilderPage({
  formName,
  formDescription,
  formCategory,
  formType,
  formFrequency = "every_time",
  validityPeriod = 12,
  requirement = "optional",
  services = [],
  sections,
  onSectionsChange,
  onFormMetaChange,
  onSave,
  onPublish,
  onCancel,
  saving,
  publishing,
  vendorName,
  vendorLogo,
}) {
  // Field config drawer state
  const [fieldDrawer, setFieldDrawer] = useState({
    open: false,
    sectionId: null,
    field: null,
  });
  // Field type selector state
  const [typeSelector, setTypeSelector] = useState({
    open: false,
    sectionId: null,
  });
  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [gateModal, setGateModal] = useState({
    open: false,
    sectionId: null,
    field: null,
    section: null,
  });
  const [pendingGateSave, setPendingGateSave] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");

  const isTemplate = formType === "predefined";

  const isCustomerOnboarding =
    isTemplate ||
    formType === "customer_onboarding" ||
    formType === "onboarding" ||
    formType === "customerOnboarding" ||
    String(formName || "")
      .trim()
      .toLowerCase() === "customer onboarding form";


      const { profileData } = useProfile();


const allowedCategories = useMemo(
  () => getPlanFormCategories(profileData),
  [profileData],
);

  const onboardingInit = useRef(false);
  useEffect(() => {
    if (!isCustomerOnboarding || onboardingInit.current) return;
    // Wait until profile/plan is loaded
    if (!allowedCategories || allowedCategories.length === 0) return;

    onboardingInit.current = true;
    onFormMetaChange({
      formName: "Customer Onboarding Form",
      formCategory: allowedCategories[0] || "", 
      formFrequency: "not_required",
      requirement: "not_required",
      services: allowedCategories, 
    });
  }, [isCustomerOnboarding, onFormMetaChange, allowedCategories]);

 const servicesSeeded = useRef(false);

 /* New form from template: every section starts with ALL plan services */
 useEffect(() => {
   if (!isCustomerOnboarding || !isTemplate) return;
   if (servicesSeeded.current) return;
   if (!sections || sections.length === 0) return;
   if (!allowedCategories || allowedCategories.length === 0) return;

   servicesSeeded.current = true;

   onSectionsChange(
     sections.map((s) =>
       isCommonBasicSection(s)
         ? {
             ...s,
             associated_services: [],
             associatedServices: [],
             associatedService: "",
           }
         : {
             ...s,
             associated_services: allowedCategories,
             associatedServices: allowedCategories,
             associatedService: allowedCategories.join(", "),
           },
     ),
   );
 }, [
   isCustomerOnboarding,
   isTemplate,
   sections,
   allowedCategories,
   onSectionsChange,
 ]);

    useEffect(() => {
      if (!isCustomerOnboarding) return;
      if (!sections || sections.length === 0) return;
      if (!allowedCategories || allowedCategories.length === 0) return;

      const allowedSet = new Set(allowedCategories);
      const allowedInterestOptions = getPlanServiceInterestOptions(profileData);

      const isUnset = (s) =>
        s.associated_services === undefined &&
        s.associatedServices === undefined &&
        !s.associatedService;

      const needsUpdate = sections.some((s) => {
        // 1. Check associated_services
        let sectionNeedsUpdate = false;
        if (isCommonBasicSection(s)) {
          sectionNeedsUpdate = isUnset(s);
        } else if (isUnset(s)) {
          sectionNeedsUpdate = true;
        } else {
          const cur = s.associated_services || s.associatedServices || [];
          sectionNeedsUpdate = cur.some((svc) => !allowedSet.has(svc));
        }
        if (sectionNeedsUpdate) return true;

        // 2. Check services_interested_in field options
        const hasInvalidInterestOpts = (s.fields || []).some((f) => {
          const mapsTo = f.mapsTo || f.maps_to;
          if (mapsTo !== "customer.services_interested_in") return false;
          return (f.options || []).some(
            (o) => !allowedInterestOptions.includes(o.label || o.value),
          );
        });

        // 3. Check pet.neutered_status field options
        const hasLegacyNeuteredOpts = (s.fields || []).some((f) => {
          const mapsTo = f.mapsTo || f.maps_to;
          const label = String(f.label || "").trim().toLowerCase();
          if (mapsTo !== "pet.neutered_status" && label !== "neutered / spayed status") return false;
          return (
            (f.options || []).length === 0 ||
            (f.options || []).some((o) => {
              const val = String(o.label || o.value || "").trim().toLowerCase();
              return val === "yes" || val === "no";
            })
          );
        });

        return hasInvalidInterestOpts || hasLegacyNeuteredOpts;
      });

      if (!needsUpdate) return;

      onSectionsChange(
        sections.map((s) => {
          let updatedSection = { ...s };

          // 1. Clean associated_services
          if (isCommonBasicSection(s)) {
            if (isUnset(s)) {
              updatedSection = {
                ...updatedSection,
                associated_services: [],
                associatedServices: [],
                associatedService: "",
              };
            }
          } else if (isUnset(s)) {
            updatedSection = {
              ...updatedSection,
              associated_services: allowedCategories,
              associatedServices: allowedCategories,
              associatedService: allowedCategories.join(", "),
            };
          } else {
            const cur = s.associated_services || s.associatedServices || [];
            const filtered = cur.filter((svc) => allowedSet.has(svc));
            if (filtered.length !== cur.length) {
              updatedSection = {
                ...updatedSection,
                associated_services: filtered,
                associatedServices: filtered,
                associatedService: filtered.join(", "),
              };
            }
          }

          // 2. Clean services_interested_in field options
          if (updatedSection.fields) {
            updatedSection.fields = updatedSection.fields.map((f) => {
              const mapsTo = f.mapsTo || f.maps_to;
              if (mapsTo === "customer.services_interested_in") {
                const cleanedOptions = (f.options || []).filter((o) =>
                  allowedInterestOptions.includes(o.label || o.value),
                );

                if (cleanedOptions.length !== (f.options || []).length) {
                  return { ...f, options: cleanedOptions };
                }
              }

              const label = String(f.label || "").trim().toLowerCase();
              if (mapsTo === "pet.neutered_status" || label === "neutered / spayed status") {
                const hasLegacy =
                  (f.options || []).length === 0 ||
                  (f.options || []).some((o) => {
                    const val = String(o.label || o.value || "").trim().toLowerCase();
                    return val === "yes" || val === "no";
                  });

                if (hasLegacy) {
                  return {
                    ...f,
                    placeholder: f.placeholder || "Select Neutered Status",
                    options: (NEUTERED_STATUS_OPTIONS || [
                      "Not Neutered",
                      "Neutered",
                      "Unknown",
                    ]).map((s) => ({ label: s, value: s })),
                  };
                }
              }
              return f;
            });
          }

          return updatedSection;
        }),
      );
    }, [
      isCustomerOnboarding,
      sections,
      onSectionsChange,
      allowedCategories,
      profileData,
    ]);
  /* Categories derive from the services array.
     We keep formCategory as the FIRST selected value for backward compat. */
  const selectedCategories =
    Array.isArray(services) && services.length > 0
      ? services
      : formCategory
        ? [formCategory]
        : [];

  const toggleCategory = (cat) => {
    const next = selectedCategories.includes(cat)
      ? selectedCategories.filter((c) => c !== cat)
      : [...selectedCategories, cat];
    onFormMetaChange({
      services: next,
      formCategory: next[0] || "",
    });
  };

  // ── Section operations ──────────────────────────────────────────────────────

  const addSection = () => {
    const newSection = {
      id: cid("sec"),
      sectionName: `New Section ${sections.length + 1}`,
      sectionDescription: "",
      displayOrder: sections.length + 1,
      collapsed: false,
      fields: [],
    };
    onSectionsChange([...sections, newSection]);
  };

  const updateSection = (updated) => {
    onSectionsChange(sections.map((s) => (s.id === updated.id ? updated : s)));
  };

  const deleteSection = (sectionId) => {
    const section = sections.find((s) => s.id === sectionId);
    if ((section?.fields || []).some(isLockedFormField)) return;
    setConfirmModal({
      open: true,
      title: "Delete Section",
      message:
        "This will permanently remove the section and all its fields. This action cannot be undone.",
      onConfirm: () => {
        onSectionsChange(sections.filter((s) => s.id !== sectionId));
        setConfirmModal({ open: false });
      },
    });
  };

  const moveSection = (index, direction) => {
    const next = [...sections];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onSectionsChange(next.map((s, i) => ({ ...s, displayOrder: i + 1 })));
  };

  const toggleCollapse = (sectionId) => {
    onSectionsChange(
      sections.map((s) =>
        s.id === sectionId ? { ...s, collapsed: !s.collapsed } : s,
      ),
    );
  };

  // ── Field operations ────────────────────────────────────────────────────────

  const openFieldTypeSelector = (sectionId) => {
    setTypeSelector({ open: true, sectionId });
  };

  const handleFieldTypeSelected = (fieldType) => {
    const fieldUid = cid("fld");
    const newField = {
      id: fieldUid,
      fieldUid,
      mapsTo: "custom",
      label: fieldType.label,
      fieldType: fieldType.value,
      required: false,
      placeholder: "",
      helpText: "",
      defaultValue: "",
      options: [],
      validation: {},
      enabled: true,
    };
    // Open config drawer immediately
    setFieldDrawer({
      open: true,
      sectionId: typeSelector.sectionId,
      field: newField,
      isNew: true,
    });
  };

  // Shared helper: write one saved field into its section
  const applyFieldSave = useCallback(
    (sectionId, savedField, isNew) => {
      onSectionsChange(
        sections.map((s) => {
          if (s.id !== sectionId) return s;
          const fields = isNew
            ? [...(s.fields || []), savedField]
            : (s.fields || []).map((f) =>
                f.id === savedField.id ? savedField : f,
              );
          return { ...s, fields };
        }),
      );
    },
    [sections, onSectionsChange],
  );

  const saveField = useCallback(
    (savedField) => {
      const { sectionId, isNew, field: original } = fieldDrawer;
      if (!isNew && isLockedFormField(original)) {
        setFieldDrawer({ open: false, sectionId: null, field: null });
        return;
      }

      if (!isNew && (isGateField(original) || isGateField(savedField))) {
        const wasEnabled = original?.enabled !== false;
        const willBeEnabled = savedField?.enabled !== false;

        if (wasEnabled && !willBeEnabled) {
          const section = sections.find((s) => s.id === sectionId) || null;

          // Preserve metadata even if the drawer dropped it
          const enriched = {
            ...savedField,
            gatesSection: true,
            sectionDisableTitle:
              savedField.sectionDisableTitle || original?.sectionDisableTitle,
            sectionDisableMessage:
              savedField.sectionDisableMessage ||
              original?.sectionDisableMessage,
            dependentFieldLabels:
              savedField.dependentFieldLabels || original?.dependentFieldLabels,
          };

          setPendingGateSave({ sectionId, savedField: enriched, isNew: false });
          setFieldDrawer({ open: false, sectionId: null, field: null });
          setGateModal({
            open: true,
            sectionId,
            field: enriched,
            section,
          });
          return;
        }
      }

      // Normal path — same as before
      applyFieldSave(sectionId, savedField, isNew);
      setFieldDrawer({ open: false, sectionId: null, field: null });
    },
    [fieldDrawer, applyFieldSave, sections],
  );

  const editField = (sectionId, field) => {
    setFieldDrawer({ open: true, sectionId, field, isNew: false });
  };

  const deleteField = (sectionId, fieldId) => {
    const section = sections.find((s) => s.id === sectionId);
    const target = (section?.fields || []).find((f) => f.id === fieldId);
    if (isLockedFormField(target)) return;
    setConfirmModal({
      open: true,
      title: "Delete Field",
      message: "Remove this field from the section? This cannot be undone.",
      onConfirm: () => {
        onSectionsChange(
          sections.map((s) => {
            if (s.id !== sectionId) return s;
            return {
              ...s,
              fields: (s.fields || []).filter((f) => f.id !== fieldId),
            };
          }),
        );
        setConfirmModal({ open: false });
      },
    });
  };

  const duplicateField = (sectionId, field) => {
    if (isLockedFormField(field)) return;
    const dup = {
      ...field,
      id: cid("fld"),
      label: `${field.label} (Copy)`,
      locked: false,
      mapsTo: "custom",
    };
    onSectionsChange(
      sections.map((s) => {
        if (s.id !== sectionId) return s;
        const idx = (s.fields || []).findIndex((f) => f.id === field.id);
        const fields = [...(s.fields || [])];
        fields.splice(idx + 1, 0, dup);
        return { ...s, fields };
      }),
    );
  };

  const moveField = (sectionId, fieldIndex, direction) => {
    onSectionsChange(
      sections.map((s) => {
        if (s.id !== sectionId) return s;
        const fields = [...(s.fields || [])];
        const target = fieldIndex + direction;
        if (target < 0 || target >= fields.length) return s;
        [fields[fieldIndex], fields[target]] = [
          fields[target],
          fields[fieldIndex],
        ];
        return { ...s, fields };
      }),
    );
  };

   const toggleFieldEnabled = (sectionId, fieldId) => {
     const section = sections.find((s) => s.id === sectionId);
     const target = (section?.fields || []).find((f) => f.id === fieldId);
     if (!section || !target) return;
     if (isLockedFormField(target)) return;

     const isCurrentlyEnabled = target.enabled !== false;
     const willEnable = !isCurrentlyEnabled;

     // ⬇️ NEW: block re-enabling a non-gate field while the gate question
     //    in the same section is still disabled.
     if (willEnable && !isGateField(target)) {
       const disabledGate = (section.fields || []).find(
         (f) => isGateField(f) && f.enabled === false,
       );
       if (disabledGate) {
         toast.warn(
           `Please enable "${disabledGate.label}" first. Fields in this section stay locked until the gate question is turned back on.`,
           { position: "top-center", autoClose: 4000 },
         );
         return; // do NOT toggle
       }
     }

     // ⬇️ Gate fields: intercept when disabling them
     if (isCurrentlyEnabled && isGateField(target)) {
       setGateModal({
         open: true,
         sectionId,
         field: target,
         section,
       });
       return;
     }

     // Normal toggle
     onSectionsChange(
       sections.map((s) => {
         if (s.id !== sectionId) return s;
         return {
           ...s,
           fields: (s.fields || []).map((f) => {
             if (f.id !== fieldId) return f;
             if (isLockedFormField(f)) return f;
             const currentEnabled = f.enabled !== false;
             return { ...f, enabled: !currentEnabled };
           }),
         };
       }),
     );
   };

   const confirmGateDisable = () => {
     const { sectionId, field } = gateModal;
     if (!sectionId || !field) return;

     const pending = pendingGateSave;

     onSectionsChange(
       sections.map((s) => {
         if (s.id !== sectionId) return s;

         // 1) Start from the section's current fields
         let fields = s.fields || [];

         // 2) If the gate was disabled via the edit drawer, commit that edit first
         if (pending && pending.sectionId === sectionId) {
           fields = fields.map((f) =>
             f.id === pending.savedField.id ? pending.savedField : f,
           );
         }

         // 3) Flip every non-locked field off
         fields = fields.map((f) => {
           if (isLockedFormField(f)) return f;
           return {
             ...f,
             enabled: false,
             is_enabled: false,
             isEnabled: false,
           };
         });

         return {
           ...s,
           enabled: false,
           is_enabled: false,
           isEnabled: false,
           fields,
         };
       }),
     );

     setPendingGateSave(null);
     setGateModal({
       open: false,
       sectionId: null,
       field: null,
       section: null,
     });
   };

  

  const cancelGateDisable = () => {
    setGateModal({
      open: false,
      sectionId: null,
      field: null,
      section: null,
    });
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Top Bar */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onCancel}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors shrink-0"
          >
            <FiX className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            {isCustomerOnboarding ? (
              <div className="text-base font-semibold text-gray-500 truncate">
                {formName || "Customer Onboarding Form"}
              </div>
            ) : (
              <input
                type="text"
                value={formName}
                onChange={(e) => onFormMetaChange({ formName: e.target.value })}
                placeholder="Form name"
                className="text-base font-semibold text-gray-800 border-0 border-b border-transparent focus:border-[#1B4582] focus:outline-none bg-transparent w-full truncate"
              />
            )}
            {formDescription && (
              <p className="text-xs text-gray-400 truncate">
                {formDescription}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setShowPreview((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              showPreview
                ? "bg-[#1B4582] text-white"
                : "border border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <FiEye className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">
              {showPreview ? "Builder" : "Preview"}
            </span>
          </button>
          {/* Save draft */}
          {/* <button
            onClick={onSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 transition-colors"
          >
            {saving ? (
              <FaSpinner className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FiSave className="w-3.5 h-3.5" />
            )}
            <span className="hidden sm:inline">Save Draft</span>
          </button> */}

          {/* Publish */}
          <button
            onClick={onPublish}
            disabled={publishing}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#1B4582] text-white rounded-lg text-sm font-semibold hover:bg-[#163a6e] disabled:opacity-60 transition-colors"
          >
            {publishing ? (
              <FaSpinner className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FiCheck className="w-3.5 h-3.5" />
            )}
            <span className="hidden sm:inline">Publish</span>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* ── Left: Builder ── */}
        <div
          className={`flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 ${
            showPreview ? "hidden" : ""
          }`}
        >
          {/* Form meta card */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">
              Form Details
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Form Name <span className="text-red-500 font-bold">*</span>
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) =>
                    onFormMetaChange({ formName: e.target.value })
                  }
                  readOnly={isCustomerOnboarding}
                  disabled={isCustomerOnboarding}
                  className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none ${
                    isCustomerOnboarding
                      ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                      : "focus:ring-2 focus:ring-[#1B4582] focus:border-transparent"
                  }`}
                  placeholder="e.g. Boarding Registration Form"
                  required
                />
                {isCustomerOnboarding && (
                  <p className="text-[11px] text-gray-400 mt-1">
                    Fixed for Customer Onboarding Form
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Categor{!isCustomerOnboarding ? "ies" : "y"}{" "}
                  <span className="text-red-500 font-bold">*</span>
                </label>
                {isCustomerOnboarding ? (
                  <>
                    <input
                      type="text"
                        value={allowedCategories.join(", ")} 
                      readOnly
                      disabled
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed outline-none"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">
                      Fixed for Customer Onboarding Form
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-1.5">
                      {(() => {
                        
                        const knownCats = new Set(allowedCategories);
                        const legacyCats = selectedCategories.filter(
                          (c) => !knownCats.has(c),
                        );
                        const catsToRender = [
                          ...legacyCats,
                          ...allowedCategories,
                        ];

                        return catsToRender.map((cat) => {
                          const selected = selectedCategories.includes(cat);
                          const isLegacy = legacyCats.includes(cat);
                          return (
                            <button
                              key={cat}
                              type="button"
                              onClick={() => toggleCategory(cat)}
                              title={
                                isLegacy
                                  ? "This category is not in your current plan"
                                  : undefined
                              }
                              className={`px-2.5 py-1 text-xs font-medium rounded-lg border transition-all ${
                                selected
                                  ? "bg-[#1B4582] text-white border-[#1B4582] shadow-sm"
                                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                              } ${isLegacy ? "opacity-70" : ""}`}
                            >
                              {selected ? `✓ ${cat}` : `+ ${cat}`}
                              {isLegacy ? " *" : ""}
                            </button>
                          );
                        });
                      })()}
                    </div>
                    {selectedCategories.length === 0 ? (
                      <p className="text-[11px] text-red-500 mt-1">
                        Select at least one category
                      </p>
                    ) : (
                      <p className="text-[11px] text-gray-400 mt-1">
                        {selectedCategories.length} selected · click a category
                        again to deselect
                      </p>
                    )}
                  </>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={formDescription}
                  onChange={(e) =>
                    onFormMetaChange({ formDescription: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B4582] focus:border-transparent outline-none"
                  placeholder="Brief description of this form"
                />
              </div>
            </div>
          </div>

          {/* ── Form Behavior Card (Matching User Specification) ── */}
          {/* ── Form Behavior Card ── */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <div>
              <h4 className="font-semibold text-gray-800 text-sm">
                Form Behavior
              </h4>
              <p className="text-xs text-gray-500 mt-0.5">
                Control when and how often this form should be completed by
                clients.
              </p>
            </div>

            {isCustomerOnboarding ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  When should this form be completed?
                </label>
                <div className="flex items-center gap-3 px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-100">
                  <input
                    type="radio"
                    checked
                    readOnly
                    disabled
                    className="w-4 h-4 text-gray-400"
                  />
                  <span className="text-sm text-gray-500">
                    Form is not required
                  </span>
                </div>
                <p className="text-[11px] text-gray-400 mt-1">
                  Fixed for Customer Onboarding Form
                </p>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    When should this form be completed?
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="formFrequency"
                        checked={
                          formFrequency === "every_time" &&
                          requirement !== "not_required"
                        }
                        onChange={() =>
                          onFormMetaChange({
                            formFrequency: "every_time",
                            requirement:
                              requirement === "not_required"
                                ? "optional"
                                : requirement,
                          })
                        }
                        className="w-4 h-4 text-[#1B4582] focus:ring-[#1B4582]"
                      />
                      <span className="text-sm text-gray-700">
                        Every time an appointment is booked
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="formFrequency"
                        checked={
                          formFrequency === "first_time" &&
                          requirement !== "not_required"
                        }
                        onChange={() =>
                          onFormMetaChange({
                            formFrequency: "first_time",
                            requirement:
                              requirement === "not_required"
                                ? "optional"
                                : requirement,
                          })
                        }
                        className="w-4 h-4 text-[#1B4582] focus:ring-[#1B4582]"
                      />
                      <span className="text-sm text-gray-700">
                        Only the first time an appointment is booked
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="formFrequency"
                        checked={
                          formFrequency === "expires_12months" &&
                          requirement !== "not_required"
                        }
                        onChange={() =>
                          onFormMetaChange({
                            formFrequency: "expires_12months",
                            requirement:
                              requirement === "not_required"
                                ? "optional"
                                : requirement,
                          })
                        }
                        className="w-4 h-4 text-[#1B4582] focus:ring-[#1B4582]"
                      />
                      <span className="text-sm text-gray-700">
                        Expires and must be completed when the selected validity
                        period ends
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="formFrequency"
                        checked={
                          formFrequency === "not_required" ||
                          requirement === "not_required"
                        }
                        onChange={() =>
                          onFormMetaChange({
                            formFrequency: "not_required",
                            requirement: "not_required",
                          })
                        }
                        className="w-4 h-4 text-[#1B4582] focus:ring-[#1B4582]"
                      />
                      <span className="text-sm text-gray-700">
                        Form is not required
                      </span>
                    </label>
                  </div>
                </div>

                {formFrequency === "expires_12months" &&
                  requirement !== "not_required" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Validity Period (Expiry)
                      </label>
                      <select
                        value={validityPeriod || 12}
                        onChange={(e) =>
                          onFormMetaChange({
                            validityPeriod: Number(e.target.value),
                          })
                        }
                        className="w-full sm:w-64 px-4 py-2.5 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-[#1B4582] focus:border-transparent transition-all"
                      >
                        {[1, 3, 6, 12, 18, 24].map((m) => (
                          <option key={m} value={m}>
                            {m} month{m > 1 ? "s" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
              </>
            )}
          </div>

          {/* Sections */}
          {sections.map((section, sIdx) => {
            const sectionEnabled = section.enabled !== false;
            return (
              <div
                key={section.id}
                className={`rounded-xl border overflow-hidden transition-colors ${
                  isCustomerOnboarding && !sectionEnabled
                    ? "bg-gray-100 border-gray-300"
                    : "bg-white border-gray-200"
                }`}
              >
                {/* Section header */}
                <div
                  className={`px-4 py-3 border-b ${
                    isCustomerOnboarding && !sectionEnabled
                      ? "bg-gray-200 border-gray-300"
                      : "bg-gray-50 border-gray-100"
                  }`}
                >
                  <SectionHeader
                    section={section}
                    onUpdate={updateSection}
                    onDelete={() => deleteSection(section.id)}
                    onMoveUp={() => moveSection(sIdx, -1)}
                    onMoveDown={() => moveSection(sIdx, 1)}
                    isFirst={sIdx === 0}
                    isLast={sIdx === sections.length - 1}
                    onToggleCollapse={() => toggleCollapse(section.id)}
                    isCustomerOnboarding={isCustomerOnboarding}
                    allowedServices={allowedCategories}
                  />
                </div>

                {/* Fields */}
                {!section.collapsed && (
                  <div className="p-4 space-y-2">
                    {(section.fields || []).map((field, fIdx) => (
                      <FieldRow
                        key={field.id}
                        field={field}
                        onEdit={() => editField(section.id, field)}
                        onDelete={() => deleteField(section.id, field.id)}
                        onDuplicate={() => duplicateField(section.id, field)}
                        onMoveUp={() => moveField(section.id, fIdx, -1)}
                        onMoveDown={() => moveField(section.id, fIdx, 1)}
                        onToggleEnabled={() =>
                          toggleFieldEnabled(section.id, field.id)
                        }
                        isFirst={fIdx === 0}
                        isLast={fIdx === (section.fields || []).length - 1}
                      />
                    ))}

                    {(section.fields || []).length === 0 && (
                      <div className="border-2 border-dashed border-gray-200 rounded-xl py-6 text-center">
                        <p className="text-sm text-gray-400">No fields yet.</p>
                      </div>
                    )}

                    <button
                      onClick={() => openFieldTypeSelector(section.id)}
                      className="mt-2 flex items-center gap-2 text-sm font-medium text-[#1B4582] hover:text-[#163a6e] transition-colors"
                    >
                      <FiPlus className="w-4 h-4" />
                      Add Field
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {!isTemplate && !isCustomerOnboarding && (
            <button
              onClick={addSection}
              className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm font-medium text-gray-500 hover:border-[#1B4582] hover:text-[#1B4582] transition-all"
            >
              <FiPlus className="w-4 h-4" />
              Add Section
            </button>
          )}
        </div>

        {showPreview && (
          <div className="flex-1 bg-gray-100 overflow-y-auto flex flex-col">
            <div className="shrink-0 px-4 py-3 bg-white border-b border-gray-200 flex items-center justify-between gap-3">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Customer preview
              </p>
              <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setPreviewMode("desktop")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${
                    previewMode === "desktop"
                      ? "bg-[#1B4582] text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <FiMonitor className="w-3.5 h-3.5" />
                  Desktop
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewMode("mobile")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${
                    previewMode === "mobile"
                      ? "bg-[#1B4582] text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <FiSmartphone className="w-3.5 h-3.5" />
                  Mobile
                </button>
              </div>
            </div>
            <div className="flex-1 p-4 overflow-y-auto">
              <div
                className={`mx-auto bg-white rounded-xl shadow-sm overflow-hidden transition-all ${
                  previewMode === "mobile"
                    ? "max-w-[390px] border border-gray-200"
                    : "max-w-3xl"
                }`}
              >
                <FormPreviewPanel
                  formName={formName}
                  formDescription={formDescription}
                  sections={sections}
                  vendorName={vendorName}
                  vendorLogo={vendorLogo}
                  previewMode={previewMode}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Field type selector */}
      <FieldTypeSelector
        isOpen={typeSelector.open}
        onClose={() => setTypeSelector({ open: false, sectionId: null })}
        onSelect={handleFieldTypeSelected}
      />

      {/* Field config drawer */}
      <FieldConfigDrawer
        isOpen={fieldDrawer.open}
        onClose={() =>
          setFieldDrawer({ open: false, sectionId: null, field: null })
        }
        field={fieldDrawer.field}
        onSave={saveField}
      />

      {/* Confirm modal */}
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ open: false })}
      />
      <GateWarningModal
        open={gateModal.open}
        field={gateModal.field}
        onConfirm={confirmGateDisable}
        onCancel={cancelGateDisable}
      />
    </>
  );
}
